/**
 * @file  contract/index.ts
 * @description  contract service to manage all the contract related operations
 *
 * @author ogarcia (ozkary)
 *
 */
import moment from 'moment';
import { Plan } from '../../functions/paymentPlan/paymentPlan.model';
import Product from '../../functions/product/product.model';
import Vehicle from '../../functions/vehicle/vehicle.model';
import { cacheCustomer } from '../../services/customer';
import {
  getDiscountExcludeStates,
  getDiscounts
} from '../../services/settings';
import * as ProductModels from '../../functions/product/product.model';
import {
  submitContract,
  setContract,
  getContractNumber,
  getContractDetails,
  getFinanceDetails
} from '../../functions/contract';
import {
  TransactionType,
  Transaction,
  Contract,
  ContractRequest,
  ContractNumberRequest,
  ContractNumber,
  ContractDetails,
  ContractToken,
  Pending
} from '../../functions/contract/contract.model';
import { Finance } from '../../functions/contract/finance.models';
import * as cache from '../cache';
import { openPdf } from '../pdf';
import * as CustomerModels from '../../functions/customer/customer.model';
import { isEmpty } from 'lodash';
import * as _log from '../logger';
import * as _signature from '../signature';
import { clearVehicleInformations } from '../customer';
import { getVehicle } from '../Vehicle';
import { IPlan } from '../plan/model';

/**
 * returns a contract record from the cache
 * @returns a contract record
 */
export const current = async (): Promise<Contract> => {
  const contract = cache.getItem<Contract>(cache.Key.contract);
  return contract;
};

/**
 * gets a new contract number which should be used for payment and submit contract operations
 * @returns a contract number
 */
export const getNewContractNumber = async (): Promise<string> => {
  let value = '';
  const contract = await current();
  const program = cache.getItem<ProductModels.default>('product');
  const programPrefix = program?.programPrefix ?? '';
  const programValue = program?.contractCode ?? '';

  if (process.env.REACT_APP_DisableApiGetContractNumber === 'true') {
    value = contract?.contractNumber ?? '';
  } else {
    if (
      contract?.pending?.number !== value ||
      contract?.pending?.prefix !== programPrefix ||
      contract?.pending?.value !== programValue
    ) {
      if (programPrefix && programValue) {
        const data: ContractNumberRequest = {
          programPrefix,
          programValue
        };

        const result: ContractNumber = await getContractNumber(data);
        value = result?.value;
        if (isEmpty(value)) {
          const msg = result?.errors?.join();
          throw new Error('Contract response is invalid ' + msg);
        }
      }
    }
  }

  const pending : Pending = {
    prefix: programPrefix,
    value: programValue,
    number: value
  };

  cache.setItem<Contract>(cache.Key.contract, {
    ...contract,
    pending: pending
  });

  return value;
};

/**
 * get pending contract number from cache
 * @returns peding contract number
 */
export const getPendingContractNumber = async (): Promise<string> => {
  const contract = await current();
  const value = contract?.pending?.number ?? '';
  return value;
};

/**
 * Build contract request object from cache
 * @param transactionType
 * @returns contract request object
 */
const buildContractRequestFromCache = async (
  transactionType: TransactionType = TransactionType.Draft
): Promise<ContractRequest> => {
  const customer = cache.getItem<CustomerModels.Customer>('contact');
  const selectecCar = customer.cars.filter((c) => c.vin === customer.vin);
  const product = cache.getItem<Product>('product');
  const plan = cache.getItem<Plan>('plan');
  const tx: Transaction = { action: transactionType };
  const signature = _signature.get() || {};
  let contractNumber;
  if (transactionType === TransactionType.Add || transactionType === TransactionType.Remit) {
    contractNumber = await getPendingContractNumber();
  }

  // use total should apply the state rule to add discounts or surcharges.
  // when discount exists use the amount instead
  const customerCost =
    (plan.totalDiscounts || 0) > 0 ? plan.amount : plan.total;

  // for testing
  // selectecCar[0].year = 2017;
  // selectecCar[0].make = 'Lexus';
  // selectecCar[0].model = 'RC';
  // selectecCar[0].newUsedIndicator = 'Used';
  // selectecCar[0].purchasePrice = 26500;
  // selectecCar[0].inService = new Date('2016-09-01');

  const contractRequest: ContractRequest = {
    customer: { ...customer, cars: selectecCar },
    product: product,
    transaction: tx,
    paymentPlan: plan,
    customerCost: customerCost, // fees/taxes should not be included in the customer cost
    signature: signature,
    contractNumber
  };

  // only cache for debugging
  if (process.env.REACT_APP_DEBUG) {
    cache.setItem<ContractRequest>('contract-request', contractRequest);
  }

  return contractRequest;
};

/**
 * saves a contract information to the server and cache
 * @param data contract information
 * @returns the saved contract information wit internal id
 */
export const save = async (data: Contract): Promise<Contract> => {
  try {
    const currentContract = await current();

    let result: Contract;

    if (process.env.REACT_APP_AllowContractPdfUpload === 'true') {
      result = await setContract(data);
    } else {
      result = await setContract({
        ...data,
        premiumFinancePDF: '',
        contractPDF: ''
      });
    }

    cache.setItem<Contract>(cache.Key.contract, {
      ...result,
      premiumFinancePDF: data.premiumFinancePDF,
      contractPDF: data.contractPDF,
      pending: currentContract?.pending ?? data?.pending,
      contractNumber: currentContract?.contractNumber ?? data?.contractNumber
    });

    return result;
  } catch (error) {
    _log.error(error);
    return data;
  }
};

/**
 * validates the required parameters. Simple validation scrub for phase I
 * @param data the contract request
 */
const validate = (data: ContractRequest) => {
  let result = true;

  if (data) {
    const customer = data.customer;
    const car = customer?.cars[0];
    const address = customer?.homeAddress;
    const tx = data.transaction;
    const plan = data.paymentPlan;
    const cost = data.customerCost;
    const planCode = data.product?.planCode;
    // TODO: validade contract number

    // shallow validation add constraints use https://ajv.js.org/
    result =
      Boolean(customer?.firstName) &&
      Boolean(car?.vin) &&
      Boolean(address?.zip) &&
      Boolean(plan?.total) &&
      Boolean(tx?.action) &&
      Boolean(cost) &&
      Boolean(planCode);

    if (!result) {
      throw new Error('Missing contract property values');
    }
  }
};

/**
 * submits a contract.
 * set customer information, transaction (review or add)
 * @param data contract request with customer information
 * @returns a contract record
 */
export const submit = async (
  transactionType: TransactionType = TransactionType.Draft
): Promise<Contract> => {
  const data = await buildContractRequestFromCache(transactionType);
  validate(data);
  const result: Contract = await submitContract(data);
  const currentContract = await current();

  if (process.env.REACT_APP_DisableApiGetContractNumber === 'true') {
    const program = cache.getItem<ProductModels.default>('product');
    const programPrefix = program?.programPrefix ?? '';
    const programValue = program?.contractCode ?? '';

    result.pending = { number: result.contractId, prefix: programPrefix, value: programValue };
    result.contractNumber = result.contractId;
  } else if (currentContract) {
    result.pending = currentContract.pending;
    result.contractNumber = currentContract.pending?.number;
  }

  await save(result);

  if (isEmpty(result.contractPDF)) {
    const msg = result?.errors?.join();
    throw new Error(
      `Contract response is invalid ${result?.contractNumber} ${msg}`
    );
  }

  return result;
};

/**
 * returns the contract number from the cache
 * @returns contract number
 */
export const code = async (): Promise<string> => {
  const contract = await current();
  return contract?.contractId;
};

/**
 * Displays the main or financial contracts
 * @param useFinancial false for main contract  , true for financial
 */
export const display = async (useFinancial = false): Promise<void> => {
  const contract = await current();
  if (contract) {
    if (useFinancial) {
      openPdf(contract.premiumFinancePDF);
    } else {
      openPdf(contract.contractPDF);
    }
  }
};

/**
 * Checks to see if a contract pdf exists
 * @returns true if found
 */
export const hasContractPdf = async (
  pContract: Contract | undefined = undefined
): Promise<boolean> => {
  const contract = pContract ?? (await current());
  return contract && !isEmpty(contract.contractPDF);
};

/**
 * Checks to see if a financial contract exists
 * @returns true if found
 */
export const hasFinancialContract = async (
  pContract: Contract | undefined = undefined
): Promise<boolean> => {
  const contract = pContract ?? (await current());
  return contract && !isEmpty(contract.premiumFinancePDF);
};

/**
 * checks if a vehicle has contracted
 * @param vin the selected vin
 * @returns true if the vehicle has contracted
 */
export const hasContracted = async (vin: string): Promise<boolean> => {
  const receipts = cache.getItem<Vehicle[]>('receipts');
  let found = false;

  if (vin && receipts && receipts.length > 0) {
    const veh = receipts.find((item) => item.vin === vin) as Vehicle;
    if (veh) {
      found = true;
    }
  }

  return found;
};

/**
 * verify if vehicle already has coverage
 * @returns flag which indicates if vehicle already has coverage
 */
export const verifyIfVehicleAlreadyHasCoverage = async (
  vin: string
): Promise<boolean> => {
  const result = await hasContracted(vin);
  return result;
};

/**
 * clear contract informations from cache leaving vehicle untouched
 */
export const clearContract = async (): Promise<void> => {
  const currentContract = cache.getItem<Contract>('contract');
  const vehicle = currentContract.vehicle;
  cache.setItem('contract', {
    vehicle: vehicle
  });

  _signature.clear(); // clears the signaure

  if (currentContract.transaction.action === TransactionType.Add || currentContract.transaction.action === TransactionType.Remit) {
    const exists = await hasContracted(vehicle.vin);
    if (!exists) {
      let receipts = cache.getItem<Vehicle[]>('receipts');
      if (!receipts) {
        receipts = [] as Vehicle[];
      }
      receipts.push(currentContract.vehicle);
      cache.setItem<Vehicle[]>('receipts', receipts);
    }
  }
};

/**
 *
 * @returns Check if contract is completed
 */
export const isContractCompleted = (): boolean => {
  const currentContract = cache.getItem<Contract>('contract');

  if (
    (currentContract?.transaction?.action === TransactionType.Add || currentContract?.transaction?.action === TransactionType.Remit) &&
    !isEmpty(currentContract?.contractPDF)
  ) {
    return true;
  }

  return false;
};

/**
 * Clear contract informations if contract is completed. it keeps informations related to vehicle
 */
export const clearContractIfCompleted = (): void => {
  const contractCompleted = isContractCompleted();

  if (contractCompleted) {
    clearContract();
    clearVehicleInformations();
  }
};

export const getContractDetailMock = (): ContractDetails => {
  const detail: ContractDetails = {
    status: true,
    message: '',
    errors: [],
    customer: {
      firstName: 'Bot',
      midName: 'Middle',
      lastName: 'Spark',
      phone: '123-123-1234',
      homeAddress: {
        address: '3100 Prospect Road',
        address2: 'Suit 201',
        city: 'Fort Lauderdale',
        state: 'FL',
        zip: '33309'
      },
      billingAdress: {
        address: '3100 Prospect Road',
        address2: 'Suit 201',
        city: 'Fort Lauderdale',
        state: 'FL',
        zip: '33309'
      },
      email: 'bot@electron.com',
      cars: [],
      payment: false,
      contract: false,
      renewal: true,
      product: {
        covCode: 'TotalCare',
        termMiles: '3000',
        termMonths: '3',
        deductibleAmount: 100,
        dealerCost: 160,
        retailCost: 2158,
        saleDate: new Date(),
        surcharges: [],
        taxRate: 0
      }
    },
    plan: {
      covCode: 'PrimaryCare',
      termMiles: '3000',
      termMonths: '3',
      deductibleAmount: 100,
      dealerCost: 160,
      retailCost: 2158,
      saleDate: new Date(),
      surcharges: [],
      taxRate: 0
    },
    vehicle: {
      model: 'RC 350 Base',
      year: 2017,
      make: 'LEXUS',
      vin: 'JTHHE5BC4H5015042',
      miles: 15000,
      source: 'v'
    },
    contract: {
      contractNumber: 'EGTB2A8A9C',
      status: 'ACTIVE',
      expirationDate: moment('2022-11-5').toDate(),
      expirationMiles: 100000,
      dealerName: 'Dealer Name',
      dealerNumber: '05000'
    }
  };

  return detail;
};

// MODE to contractDetails method
export const validateContractDetails = async (
  details: ContractDetails,
  vin: string
): Promise<boolean> => {
  if (details?.vehicle?.vin !== vin) {
    return Promise.resolve(false);
  }
  return Promise.resolve(true);
};

/**
 * gets the contract details.
 * @param contracNumber the contract number to load
 * @returns the contract details with customer, plan, contract and vehicle information
 */
export const contractDetails = async (
  contracNumber: string,
  vin: string
): Promise<ContractDetails> => {
  const currentContract = cache.getItem<ContractDetails>(
    cache.Key.currentContract
  );
  const currentCustomer = cache.getItem<CustomerModels.Customer>(
    cache.Key.contact
  );
  let status = 'token-no-contract';
  const currentVehicle = cache.getItem<Vehicle>(cache.Key.vehicle);
  const details = currentContract ?? (await getContractDetails(contracNumber));

  // check the status and cache only when found
  if (details?.status === true) {
    status = 'token-contract-found';
    // valiate the vin
    if (
      vin?.length < 6 ||
      details.vehicle.vin?.toLowerCase().indexOf(vin?.toLowerCase()) === -1
    ) {
      status = 'token-wrong-vin';
      details.status = false;
    }

    // validate if contract is expired or active
    if (
      details?.contract?.status.toLowerCase() !== 'active' &&
      details?.contract?.status.toLowerCase() !== 'expired'
    ) {
      status = 'token-not-active';
      details.status = false;
    }

    const now = moment(new Date());
    const end = moment(details.contract.expirationDate);
    // expiration date may be in the future for expired contract override the date to enable discount logic
    if (
      details?.contract?.status?.toLowerCase() === 'expired' &&
      end.diff(now) >= 0
    ) {
      // set expr date to yesterday
      details.contract.expirationDate = moment().add(-1, 'days').toDate();
    }

    const updatedCustomer = { ...currentCustomer, ...details.customer };
    updatedCustomer.homeAddress = {
      ...updatedCustomer.homeAddress,
      state: currentVehicle?.state ?? details?.customer?.homeAddress?.state,
      zip: currentVehicle?.zip ?? details?.customer?.homeAddress?.zip
    };

    await cacheCustomer(updatedCustomer);
    cache.setItem<ContractDetails>(cache.Key.currentContract, details);
  }

  // remove PII data for analytics
  const nonPiiDetails: ContractDetails = { ...details };
  nonPiiDetails.customer = {} as CustomerModels.Customer;
  _log.audit<ContractDetails>(status, details, nonPiiDetails);

  return details;
};

/**
 * Gets current contract that is going to renew
 * @returns Current contract.
 */
export const getCurrentContract = (): ContractDetails =>
  cache.getItem<ContractDetails>(cache.Key.currentContract);

/**
 * gets the token from the url and loads the contract token information
 * url: token=####
 * @returns contract token information
 */
export const getContractToken = async (): Promise<ContractToken | null> => {
  const currentContract = await getCurrentContract();

  if (currentContract) {
    return {
      vin: currentContract.vehicle.vin,
      id: currentContract.contract.contractNumber
    };
  }

  const token = new URLSearchParams(window.location.search).get('token') || '';
  let result: ContractToken | null = null;

  if (token) {
    // decode the token and load the json
    const text = atob(token);
    result = JSON.parse(text);
    if (
      result === undefined ||
      result?.vin === undefined ||
      result?.id === undefined
    ) {
      _log.audit<string>('token-invalid', text);
      return null;
    }
    result.token = text;

    _log.audit<ContractToken>('token-found', result);
  }

  return result;
};

/**
 * Gets discount.
 * @returns Discount.
 */
export const getDiscount = async (state?: string): Promise<number> => {
  const contract = getCurrentContract();
  const currentVehicle = getVehicle();
  const discounts = (await getDiscounts()) ?? [];
  const defaultDiscount = 0;
  const discountExcludeStates = (await getDiscountExcludeStates()) ?? [];
  let result = defaultDiscount;

  const selectedState =
    state ?? currentVehicle?.state ?? contract?.customer?.homeAddress?.state;

  if (contract) {
    const now = moment(new Date());
    const end = moment(contract.contract.expirationDate);
    const days = end.diff(now, 'days');
    const discount = discounts.find((x) => days >= x.min && days <= x.max);
    result = discount?.amount ?? defaultDiscount;

    // return 0 when we have discount and we are in a state that doesnt have discount
    if (
      result < 0 &&
      discountExcludeStates &&
      discountExcludeStates.find((x) => x === selectedState)
    ) {
      return 0;
    }
  }

  return result;
};

/**
 * Gets financial information that can be use to calculate the downpayment and fees
 * @param saleDate today's date
 * @param amount amount to filter fees by. No amount returns multiple fees
 * @returns Financial details with fees and downpayment
 */
export const financeDetails = async (
  saleDate: string,
  amount?: number
): Promise<Finance> => {
  const finance = cache.getItem<Finance>(cache.Key.finance);
  const details = finance ?? (await getFinanceDetails(saleDate, amount));

  // check the status and cache only when found
  if (details?.status?.value) {
    cache.setItem<Finance>(cache.Key.finance, details);
  } else {
    const message = details?.status?.message;
    throw new Error(`financeDetails - ${message}`);
  }
  return details;
};

/**
 * Check if current contract is expired
 */
export const isCurrentContractExpired = (): boolean => {
  const currentContract = cache.getItem<ContractDetails>(
    cache.Key.currentContract
  );

  const now = moment(new Date());
  const end = moment(currentContract?.contract?.expirationDate);

  if (end.diff(now) < 0) {
    return true;
  }

  return false;
};

/**
 * Clear contractPDF and premiumFinancePDF from current contract
 */
export const clearPdf = async (): Promise<void> => {
  try {
    const currentContract = await current();

    cache.setItem<Contract>(cache.Key.contract, {
      ...currentContract,
      premiumFinancePDF: '',
      contractPDF: ''
    });
  } catch (error) {
    _log.error(error);
  }
};

/**
 * Gets transaction type based on payment
 */
export const getTransactionType = (): TransactionType => {
  const plan = cache.getItem<IPlan>(cache.Key.plan);
  if (plan.id === 'single-payment') {
    return TransactionType.Remit;
  }

  return TransactionType.Add;
};
